import { useState, useEffect } from "react";
import { AlertTriangle, LogOut, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ImpersonationBanner() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkImpersonationState = () => {
      const isImp = localStorage.getItem("isImpersonating") === "true";
      const uStr = localStorage.getItem("user");
      let uObj: any = null;
      if (uStr) {
        try {
          uObj = JSON.parse(uStr);
        } catch (e) { }
      }

      setIsImpersonating(isImp || Boolean(uObj?.isImpersonating));
      setUser(uObj);
    };

    checkImpersonationState();

    const handleStorageChange = () => checkImpersonationState();
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  if (!isImpersonating) return null;

  const getAdminPortalUrl = () => {
    if (process.env.REACT_APP_ADMIN_URL) {
      return process.env.REACT_APP_ADMIN_URL;
    }
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const currentPort = window.location.port;
      const isLocalHostOrIp = host === "localhost" || host === "127.0.0.1" || /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host);
      if (isLocalHostOrIp) {
        const targetPort = currentPort === '3001' ? '3000' : '3001';
        return `${window.location.protocol}//${host}:${targetPort}/dashboard`;
      }
      return `${window.location.protocol}//${host}/admin/dashboard`;
    }
    return "https://straightdoc.info/admin/dashboard";
  };

  const handleExitImpersonation = () => {
    try {
      const adminToken = localStorage.getItem("adminToken");
      if (adminToken) {
        localStorage.setItem("token", adminToken);
        localStorage.removeItem("adminToken");
      }
      localStorage.removeItem("isImpersonating");
      localStorage.removeItem("user");

      // Redirect back to Admin Portal
      const adminUrl = getAdminPortalUrl();
      if (window.opener && !window.opener.closed) {
        window.close();
      } else {
        window.location.href = adminUrl;
      }
    } catch (e) {
      window.location.href = getAdminPortalUrl();
    }
  };

  const userName = user?.displayName || (user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email || "User");
  const userRole = user?.role || "USER";

  return (
    <div className="bg-gradient-to-r from-primary via-indigo-600 to-cyan-600 text-white px-4 py-2 text-xs font-semibold shadow-md flex items-center justify-between z-50 sticky top-0 left-0 right-0 border-b border-white/10">
      <div className="flex items-center gap-2 max-w-[1600px] mx-auto w-full justify-between">
        <div className="flex items-center gap-2 truncate">
          <UserCheck className="w-4 h-4 shrink-0 animate-pulse text-cyan-200" />
          <span className="truncate">
            <span className="font-bold uppercase tracking-wider text-[11px] bg-white/15 text-white px-2 py-0.5 rounded-md mr-1.5 border border-white/20">
              Impersonating Session
            </span>
            Viewing as <strong className="underline decoration-cyan-200/60 decoration-2 underline-offset-2">{userName}</strong> ({userRole})
          </span>
        </div>

        <Button
          type="button"
          size="sm"
          onClick={handleExitImpersonation}
          className="h-7 bg-white/90 hover:bg-white text-slate-900 font-bold text-[11px] px-3 rounded-lg flex items-center gap-1.5 border border-white/30 shrink-0 transition cursor-pointer shadow-sm"
        >
          <LogOut className="w-3.5 h-3.5 text-indigo-600" />
          Exit & Return to Admin
        </Button>
      </div>
    </div>
  );
}
